import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

// Native ERP port of the standalone Sales Reporter app's "Profile" tab.
// Mirrors api/me.php's display fields (name/email/role/territory —
// already served by Phase 1's GET /me, no backend change needed here)
// plus, for GM users only, an admin section built directly against
// api/get_settings.php / api/update_settings.php and the adapted
// api/get_members.php / api/set_member_active.php / api/delete_member.php.
//
// No logout button here by design — the ERP's existing logout (in its
// own layout/header) is what signs a user out; this page only ever
// manages this module's own opt-in state.
//
// IMPORTANT on "remove from sales module": this only ever clears this
// user's sales_role/territory/sales_active/sales_opted_in_at fields and
// deletes their own sales_visits/sales_expenses/sales_plans documents.
// It never touches their ERP account, email, password, or their access
// to any other module — see backend/sales_report.py's Phase 5 module
// docstring for the complete audit of every write this module makes to
// the shared `users` collection.
export default function SalesProfilePage() {
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [members, setMembers] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const isGm = profile?.sales_role === "gm";

  const loadProfile = () => api.get("/sales-report/me").then(({ data }) => setProfile(data));
  const loadSettings = () => api.get("/sales-report/settings").then(({ data }) => {
    setSettings(data.settings);
    setSettingsForm(data.settings);
  });
  const loadMembers = () => api.get("/sales-report/members").then(({ data }) => setMembers(data)).catch(() => {});

  useEffect(() => { loadProfile(); }, []);
  useEffect(() => {
    if (!profile) return;
    loadSettings();
    if (isGm) loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.sales_role]);

  const saveSettings = async () => {
    const payload = {};
    if (settingsForm.car_rate_per_km !== settings.car_rate_per_km) payload.car_rate_per_km = Number(settingsForm.car_rate_per_km);
    if (settingsForm.bike_rate_per_km !== settings.bike_rate_per_km) payload.bike_rate_per_km = Number(settingsForm.bike_rate_per_km);
    if (settingsForm.company_name !== settings.company_name) payload.company_name = settingsForm.company_name;
    if (Object.keys(payload).length === 0) return toast.info("Nothing to update");
    setSavingSettings(true);
    try {
      await api.post("/sales-report/settings", payload);
      toast.success("Settings updated");
      await loadSettings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.put(`/sales-report/members/${m.id}/active`, { active: !m.sales_active });
      loadMembers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(
      `Remove ${m.name} from the Sales Report module?\n\nThis permanently deletes their visits, expenses and plans. Their ERP account and login are not affected.`
    )) return;
    try {
      await api.delete(`/sales-report/members/${m.id}`);
      toast.success(`${m.name} removed from the sales module`);
      loadMembers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!profile) return null;

  return (
    <div className="space-y-6" data-testid="sales-profile-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales Report</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Profile
        </h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-2 gap-4 max-w-xl">
        <div>
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500">Name</div>
          <div className="text-sm font-medium text-slate-800 mt-0.5" data-testid="profile-name">{profile.name}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500">Email</div>
          <div className="text-sm font-medium text-slate-800 mt-0.5" data-testid="profile-email">{profile.email}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500">Role</div>
          <div className="text-sm font-medium text-slate-800 mt-0.5" data-testid="profile-role">{profile.sales_role_label || "Not opted in"}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500">Territory</div>
          <div className="text-sm font-medium text-slate-800 mt-0.5" data-testid="profile-territory">{profile.territory || "—"}</div>
        </div>
      </div>

      {isGm && settingsForm && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 max-w-xl space-y-4" data-testid="admin-settings-section">
          <div className="text-sm font-semibold text-slate-700">Admin — Rates & Company</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Car Rate (₹/km)</Label>
              <Input type="number" min="0" value={settingsForm.car_rate_per_km}
                onChange={(e) => setSettingsForm({ ...settingsForm, car_rate_per_km: e.target.value })}
                data-testid="settings-car-rate-input" />
            </div>
            <div>
              <Label>Bike Rate (₹/km)</Label>
              <Input type="number" min="0" value={settingsForm.bike_rate_per_km}
                onChange={(e) => setSettingsForm({ ...settingsForm, bike_rate_per_km: e.target.value })}
                data-testid="settings-bike-rate-input" />
            </div>
          </div>
          <div>
            <Label>Company Name</Label>
            <Input value={settingsForm.company_name}
              onChange={(e) => setSettingsForm({ ...settingsForm, company_name: e.target.value })}
              data-testid="settings-company-name-input" />
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn">
            Save Settings
          </Button>
        </div>
      )}

      {isGm && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto" data-testid="team-members-section">
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-slate-700">Team Members</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                <th className="py-3 px-4">Name</th>
                <th className="py-3 px-4">Email</th>
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-4">Territory</th>
                <th className="py-3 px-4">Active</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isSelf = m.id === profile.id;
                return (
                  <tr key={m.id} className="border-t border-slate-100" data-testid={`member-row-${m.id}`}>
                    <td className="py-3 px-4 text-slate-800">{m.name}{isSelf && <span className="text-xs text-slate-400"> (you)</span>}</td>
                    <td className="py-3 px-4 text-slate-600 text-xs">{m.email}</td>
                    <td className="py-3 px-4 text-xs uppercase text-slate-500">{m.role}</td>
                    <td className="py-3 px-4 text-slate-600 text-xs">{m.territory || "—"}</td>
                    <td className="py-3 px-4">
                      <Switch checked={m.sales_active} disabled={isSelf && m.sales_active} onCheckedChange={() => toggleActive(m)} data-testid={`toggle-active-${m.id}`} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button variant="ghost" size="icon" disabled={isSelf} onClick={() => removeMember(m)} data-testid={`remove-member-${m.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {members.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-slate-500 text-sm">No team members yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
